'use client'

import { useEffect, useState } from 'react'
import {
  Listing,
  Product,
  User,
  useCreateLikeMutation,
  useCurrentUserQuery,
  useDeleteLikeMutation,
} from '@/api'
import LikeIcon from '@/app/_components/icons/LikeIcon'
import { useGTMTracking } from '@/hooks/useGTMTracking'

interface LikeButtonProps {
  listing?: Listing | null
  isLiked?: boolean
  likeCount?: number
  className?: string
  disabled?: boolean
  showCount?: boolean
  onLikeSuccess?: () => void
}

export const LikeButton = ({
  listing,
  isLiked = false,
  likeCount = 0,
  className = '',
  disabled = false,
  showCount = true,
  onLikeSuccess,
}: LikeButtonProps) => {
  const listingId = listing?.id
  const [optimisticLiked, setOptimisticLiked] = useState(isLiked)
  const [optimisticCount, setOptimisticCount] = useState(likeCount)

  const { data: { currentUser } = {} } = useCurrentUserQuery()
  const { trackEvent } = useGTMTracking()
  const [createLike, { loading: creating }] = useCreateLikeMutation()
  const [deleteLike, { loading: deleting }] = useDeleteLikeMutation()

  const loading = creating || deleting

  useEffect(() => {
    setOptimisticLiked(isLiked)
  }, [isLiked])

  useEffect(() => {
    setOptimisticCount(likeCount)
  }, [likeCount])

  const handleLike = async () => {
    if (disabled || !listingId || loading) return

    const newLikedState = !optimisticLiked
    setOptimisticLiked(newLikedState)
    setOptimisticCount((prev) => (newLikedState ? prev + 1 : prev - 1))

    try {
      if (newLikedState) {
        await createLike({
          variables: { attributes: { listingId } },
        })
        trackEvent(
          'add_to_favorites',
          (listing?.product as Product) ?? null,
          (currentUser as User) ?? null,
          null,
          (listing as Listing) ?? null
        )
      } else {
        await deleteLike({
          variables: { attributes: { listingId } },
        })
      }

      onLikeSuccess?.()
    } catch (error) {
      setOptimisticLiked(optimisticLiked)
      setOptimisticCount(optimisticCount)
      console.error('Like error:', error)
    }
  }

  return (
    <div className={`flex flex-row items-center gap-2 ${className} relative`}>
      <div className="relative group">
        <LikeIcon
          onClick={disabled ? undefined : handleLike}
          className={`w-6 h-6 lg:w-8 lg:h-8 ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          fill={optimisticLiked ? '#000000' : 'transparent'}
          stroke={
            disabled ? '#CCCCCC' : optimisticLiked ? '#000000' : '#4D4D4D'
          }
          strokeWidth="2"
        />
        {disabled && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-700 text-gray-200 text-xs rounded-sm opacity-0 group-hover:opacity-75 transition-opacity duration-300 whitespace-nowrap pointer-events-none z-10">
            ログインが必要です
          </div>
        )}
      </div>
      {showCount && (
        <span className="text-sm lg:text-2xl text-[#4D4D4D] text-center">
          {optimisticCount}
        </span>
      )}
    </div>
  )
}
